import moment from "moment-timezone";
import { NaiveDateTime, NaiveRange } from "./datetime/index.js";
/*
  api::bizHours
  [
    {open: 600, close: 1200}, // Monday : iso week day 1
    {open: 600, close: 1200}, // 600 (minutes) => "10:00", 1200 => "20:00"
    {open: 1320, close: 360}, // open time is a referance date. It means store opens at 22:00 Wendsday and closes at 06:00 Thursday.
    {open: 600, close: 1200},
    {open: 600, close: 1200},
    {open: 0, close: 0},      // "00:00" ~ "00:00" means store is closed on the day of week.
    {open: 720, close: 1440}  // Sunday
  ],

  store::businessHours
  {
    active: true,
    dayOfWeek: [
      {open: "09:00",close: "20:00"},
      {open: "09:00",close: "20:00"},
      {open: "09:00",close: "20:00"},
      {open: "09:00",close: "20:00"},
      {open: "09:00",close: "20:00"},
      {open: "09:00",close: "20:00"},
      {open: "09:00",close: "20:00"}
    ]
  }
*/
export class BizSchedule {
    timezone;
    bizHours;
    holidays;
    workdays;
    constructor(schedule, timezone) {
        this.timezone = timezone;
        if ("bizHours" in schedule) {
            this.bizHours = schedule.bizHours;
            this.holidays = schedule.holidays || [];
            this.workdays = schedule.workdays || [];
        }
        else {
            this.bizHours = convertStoreBH2ApiBH(schedule.businessHours);
            this.holidays = [];
            this.workdays = [];
        }
    }
    isOpen(at, isRoundTime = false) {
        return this.isBizHours(at, isRoundTime) && !this.isHoliday(at);
    }
    isBizHours(at, isRoundTime = false) {
        const bh = this.getBizHoursOfDay(at);
        let { open, close } = bh;
        if (isRoundTime) {
            const roundedTime = getRoundTime(bh);
            open = roundedTime.open;
            close = roundedTime.close;
        }
        if (open == close)
            return false;
        const mdx = at.hours() * 60 + at.minutes();
        const rs = bh.overnight ? mdx >= open || mdx < close : open <= mdx && mdx < close;
        return rs;
    }
    isHoliday(at) {
        const bh = this.getBizHoursOfDay(at);
        const date = at.format("YYYY-MM-DD");
        const isWorkDate = this.workdays.indexOf(date) >= 0;
        if (isWorkDate)
            return false;
        if (bh.open == bh.close)
            return true;
        return this.holidays.indexOf(date) >= 0;
    }
    getBizHoursOfDay(at) {
        const isowd = at.isoWeekday() - 1; // isoweekday 1 => Monday
        return this.bizHours[isowd];
    }
    getRangeOfDay(day) {
        const at = day.toMoment(this.timezone);
        const bizHour = this.getBizHoursOfDay(at);
        if (!bizHour.active) {
            return new NaiveRange(NaiveDateTime.fromMoment(at).startOf("day"), NaiveDateTime.fromMoment(at).startOf("day").plus({ day: 1 }), "[]");
        }
        const from = NaiveDateTime.fromMoment(at).set({
            hour: Math.floor(bizHour.open / 60),
            minute: Math.floor(bizHour.open % 60),
            second: 0,
            millisecond: 0,
        });
        let to = NaiveDateTime.fromMoment(at).set({
            hour: Math.floor(bizHour.close / 60),
            minute: Math.floor(bizHour.open % 60),
            second: 0,
            millisecond: 0,
        });
        if (bizHour.overnight) {
            to = to.plus({ day: 1 });
        }
        return new NaiveRange(from, to, "[]");
    }
    setICSDates(holidays, workdays) {
        this.holidays = holidays;
        this.workdays = workdays;
    }
}
const convertStrTimeToMinutes = (bh) => {
    const open = moment.duration(bh.open).asMinutes();
    const close = moment.duration(bh.close).asMinutes();
    return {
        active: bh.open != bh.close,
        overnight: open > close,
        open,
        close,
    };
};
const convertStoreBH2ApiBH = (sbh) => {
    if (!sbh.active || sbh.dayOfWeek.length === 0) {
        return Array(7).fill({ overnight: false, open: 0, close: 1440 });
    }
    else {
        if (sbh.dayOfWeek.length === 1) {
            return Array(7).fill(convertStrTimeToMinutes(sbh.dayOfWeek[0]));
        }
        else if (sbh.dayOfWeek.length === 2) {
            const wd = Array(5).fill(convertStrTimeToMinutes(sbh.dayOfWeek[0]));
            const we = Array(2).fill(convertStrTimeToMinutes(sbh.dayOfWeek[1]));
            return wd.concat(we);
        }
        else if (sbh.dayOfWeek.length === 7) {
            return sbh.dayOfWeek.map(convertStrTimeToMinutes);
        }
        else {
            throw new Error("day of week number is incorrect");
        }
    }
};
// open: 10:30 => 10:00
// close: 20:30 => 21:00
const getRoundTime = (bh) => {
    const open = bh.open - (bh.open % 60);
    let close = bh.close;
    if (close % 60 > 0) {
        close += 60 - (close % 60);
    }
    return { open, close };
};
export default BizSchedule;
